const signedUrlCache = new Map();
const SIGNED_URL_TTL_MS = 5 * 60 * 1000;
const hydrateInFlight = new Map();

// Export for testing purposes
export function clearSignedUrlCache() {
  signedUrlCache.clear();
  hydrateInFlight.clear();
}

export async function resolveFileLinkHelper(file, selectedCase, fetchSignedUrlFn, fetchCaseByIdFn) {
  if (!file || !selectedCase) return null;

  const resolveFromObjectKey = async (objectKey) => {
    if (!objectKey) return null;

    // Check cache first
    const cached = signedUrlCache.get(objectKey);
    if (cached && cached.expires > Date.now()) {
      return cached.url;
    }

    // Fetch fresh signed URL from API
    try {
      const fresh = await fetchSignedUrlFn(objectKey);

      // Try different response formats
      const url = fresh?.data?.url ||
                  fresh?.data?.signedUrl ||
                  fresh?.url ||
                  fresh?.signedUrl ||
                  (typeof fresh === 'string' ? fresh : null);


      if (url) {
        signedUrlCache.set(objectKey, { url, expires: Date.now() + SIGNED_URL_TTL_MS });
        return url;
      }
      throw new Error("Unable to load document preview.");
    } catch (error) {
      console.error("Failed to fetch signed URL for objectKey:", objectKey, error);
      throw error;
    }
  };

  // Priority 1: Use objectKey if available
  if (file?.objectKey) {
    try {
      const freshUrl = await resolveFromObjectKey(file.objectKey);
      if (freshUrl) return freshUrl;
    } catch (error) {
      console.error("Error resolving document URL:", error);
      throw new Error("Unable to load document preview.");
    }
  }

  // Priority 2: Fetch case by ID to get complete document metadata with objectKey
  if (!file?.objectKey && selectedCase?.id) {
    try {
      const cacheKey = selectedCase.id;
      let casePromise = hydrateInFlight.get(cacheKey);
      if (!casePromise) {
        casePromise = fetchCaseByIdFn(selectedCase.id);
        hydrateInFlight.set(cacheKey, casePromise);
      }
      const caseResp = await casePromise;
      hydrateInFlight.delete(cacheKey);
      const latestCase = caseResp?.data || {};
      const docs = latestCase.documents || latestCase.files || [];

      // Try to match by name or objectKey contains filename
      const matched = docs.find(
        (doc) =>
          doc.objectKey &&
          (doc.name === file.name ||
            doc.fileName === file.name ||
            doc.originalName === file.name ||
            doc.objectKey.includes(file.name))
      );

      if (matched?.objectKey) {
        const freshUrl = await resolveFromObjectKey(matched.objectKey);
        if (freshUrl) return freshUrl;
      }
    } catch (error) {
      console.error("Unable to refresh document metadata:", error);
    }
  }

  // No objectKey found - cannot generate signed URL
  throw new Error("Unable to load document preview.");
}
